import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PostCard from "../components/PostCard";
import NewsletterSignup from "../components/NewsletterSignup";
import { Instagram, ShieldCheck, ChevronRight } from "lucide-react";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { WHATSAPP_URL, INSTAGRAM_URL } from "../lib/constants";
import { usePageMeta } from "../lib/usePageMeta";

export default function AuthorPage() {
  const { slug } = useParams();
  const [pageTitle, setPageTitle] = useState(null);
  usePageMeta(pageTitle);
  const [author, setAuthor] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/authors/${slug}`)
      .then((r) => {
        setAuthor(r.data.author);
        setPosts(r.data.posts || []);
        document.title = `${r.data.author.name} — Viagens Orion`;
        setPageTitle(document.title);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (notFound) return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="container-orion py-32 text-center flex-1">
        <div className="cat-chip mb-4">404</div>
        <h1 className="font-serif text-4xl text-orion-purple mb-4">Autor não encontrado</h1>
        <Link to="/" className="text-orion-orange uppercase tracking-widest text-xs font-semibold">Voltar para a home</Link>
      </main>
      <Footer />
    </div>
  );

  if (loading || !author) return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="container-orion py-32 text-center text-[#8A8191] flex-1">Carregando…</div>
      <Footer />
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1" data-testid="author-page">
        {/* Hero */}
        <section className="relative bg-gradient-to-br from-[#2C1139] via-[#3d1b52] to-[#1c0a25] text-white overflow-hidden">
          <span className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-orion-orange/25 blur-3xl" aria-hidden />
          <span className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-orion-gold/20 blur-3xl" aria-hidden />

          <div className="container-orion py-16 md:py-24 relative">
            <div className="text-xs uppercase tracking-[0.24em] text-orion-gold flex items-center gap-2 mb-6">
              <Link to="/" className="hover:text-white">Início</Link>
              <ChevronRight size={12} />
              <span>Autores</span>
              <ChevronRight size={12} />
              <span className="text-white">{author.name}</span>
            </div>

            <div className="grid md:grid-cols-[220px,1fr] gap-8 md:gap-12 items-center">
              <div className="relative mx-auto md:mx-0">
                <span className="absolute -inset-2 rounded-full bg-gradient-to-br from-orion-gold via-orion-orange to-orion-gold opacity-70" aria-hidden />
                <img
                  src={author.photo_url}
                  alt={author.name}
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                  width="224"
                  height="224"
                  className="relative h-52 w-52 md:h-56 md:w-56 object-cover rounded-full ring-4 ring-white/20 shadow-[0_18px_50px_rgba(0,0,0,0.45)]"
                  style={{ objectPosition: author.photo_position || "center" }}
                  data-testid="author-hero-photo"
                />
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold">
                  {author.short_role}
                </div>
                <h1 className="mt-2 font-serif text-2xl md:text-6xl leading-[1.1] md:leading-[1.05]">
                  {author.name}
                </h1>
                <p className="mt-4 text-white/85 text-base md:text-lg leading-relaxed max-w-2xl">
                  {author.bio}
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href={WHATSAPP_URL}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-[#25D366] px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#1EBE57] transition-colors"
                    data-testid="author-wa-btn"
                  >
                    <WhatsAppIcon size={14} color="#ffffff" /> Falar com {author.name.split(" ")[0]}
                  </a>
                  <a
                    href={author.instagram || INSTAGRAM_URL}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 border border-white/25 px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] font-semibold hover:border-orion-orange hover:text-orion-orange transition-colors"
                    data-testid="author-ig-btn"
                  >
                    <Instagram size={14} /> @viagensorion
                  </a>
                </div>
                <div className="mt-5 flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/60">
                  <ShieldCheck size={12} className="text-orion-gold" /> Cadastur · CNPJ 37.476.039/0001-90
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Posts do autor */}
        <section className="container-orion py-16">
          <div className="cat-chip mb-4">Posts assinados</div>
          <h2 className="font-serif text-xl md:text-4xl text-orion-purple mb-8 md:mb-10">
            Os últimos roteiros de {author.name.split(" ")[0]}
          </h2>
          {posts.length === 0 ? (
            <p className="text-[#8A8191]">Nenhum post publicado ainda.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10">
              {posts.map((p) => <PostCard key={p.id} post={p} />)}
            </div>
          )}
        </section>

        {/* Newsletter */}
        <div className="container-orion pb-16 max-w-3xl mx-auto">
          <NewsletterSignup variant="editorial" />
        </div>
      </main>
      <Footer />
    </div>
  );
}
